import { useNavigate, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { DataTable, type DataTableColumn } from '../../_shared/DataTable';
import { PaginationControls } from '../../_shared/PaginationControls';
import { useCampaignList } from '../hooks/useCampaignList';
import {
  CURRENCY,
  COMPACT_NUMBER,
  NUMBER,
  PERCENT,
  RATIO_X,
} from '../../_shared/format';
import type {
  PerformanceCampaignRow,
  PerformanceCampaignSortField,
  PerformanceSortDir,
} from '@/services/api';

export function CampaignTable() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, page, pageSize, sortField, sortDir } = useCampaignList();

  const rows = data?.rows ?? [];
  const hasNext = rows.length === pageSize;
  const hasPrev = page > 0;

  function handleSortChange(field: string, dir: 'asc' | 'desc') {
    const next = new URLSearchParams(searchParams);
    next.set('sort_field', field);
    next.set('sort_dir', dir);
    next.delete('page');
    setSearchParams(next, { replace: true });
  }

  function handlePagination(delta: number) {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(Math.max(0, page + delta)));
    setSearchParams(next, { replace: true });
  }

  const columns: DataTableColumn<PerformanceCampaignRow>[] = [
    {
      key: 'campaign_name',
      label: 'Campaign',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-navy-900" title={row.campaign_name}>
          {row.campaign_name}
        </span>
      ),
    },
    {
      key: 'objective',
      label: 'Objective',
      width: '110px',
      render: (row) =>
        row.objective ? (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono text-slate-600">
            {row.objective.replace(/^OUTCOME_/, '')}
          </span>
        ) : null,
    },
    {
      key: 'spend',
      label: 'Spend',
      align: 'right',
      sortable: true,
      width: '110px',
      render: (row) => CURRENCY.format(row.spend),
    },
    {
      key: 'impressions',
      label: 'Impr.',
      align: 'right',
      sortable: true,
      width: '90px',
      render: (row) => COMPACT_NUMBER.format(row.impressions),
    },
    {
      key: 'ctr',
      label: 'CTR',
      align: 'right',
      sortable: true,
      width: '80px',
      render: (row) => PERCENT(row.ctr),
    },
    {
      key: 'purchases',
      label: 'Purch.',
      align: 'right',
      sortable: true,
      width: '80px',
      render: (row) => NUMBER.format(row.purchases),
    },
    {
      key: 'roas',
      label: 'ROAS',
      align: 'right',
      sortable: true,
      width: '80px',
      render: (row) => (
        <span
          className={clsx(
            row.roas >= 1.5
              ? 'text-emerald-600'
              : row.roas >= 0.5
              ? 'text-slate-700'
              : 'text-amber-600',
          )}
        >
          {RATIO_X(row.roas)}
        </span>
      ),
    },
    {
      key: 'cpa',
      label: 'CPA',
      align: 'right',
      sortable: true,
      width: '90px',
      render: (row) => CURRENCY.format(row.cpa),
    },
    {
      key: 'top_persona',
      label: 'Top Persona',
      width: '170px',
      render: (row) =>
        row.top_persona ? (
          <span className="text-slate-700" title={row.top_persona}>
            {row.top_persona}{' '}
            <span className="text-[10px] text-slate-400">
              ({PERCENT(row.top_persona_share_pct, 0)})
            </span>
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
  ];

  return (
    <div>
      <DataTable<PerformanceCampaignRow>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.campaign_id}
        loading={loading}
        emptyText="No campaigns match your filters."
        sortField={sortField as string}
        sortDir={sortDir as PerformanceSortDir}
        onSortChange={(f, d) =>
          handleSortChange(f as PerformanceCampaignSortField, d)
        }
        onRowClick={(row) => {
          // Preserve current search/sort/date params via the URL state.
          navigate(
            `/performance/campaigns/${encodeURIComponent(row.campaign_id)}?${searchParams.toString()}`,
          );
        }}
      />
      <PaginationControls
        page={page}
        pageSize={pageSize}
        totalCount={rows.length}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onPrev={() => handlePagination(-1)}
        onNext={() => handlePagination(1)}
      />
    </div>
  );
}
